import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { handleLogout } from '../../controllers/authController';
import { handleGetRequests } from '../../controllers/requestController';
import { handleCreateQuote, handleGetQuotes } from '../../controllers/quoteController';
import { handleGetSubMerchant, handleSaveSubMerchant, handleGetOrders } from '../../controllers/paymentController';
import { getConversations, getMessages, sendMessage as apiSendMessage,
         createStock, getMyStock, deleteStock, requestAdminHelp, startConversation, updateProfile, getSupplierReviews,
         uploadTaxCertificate, addStockImages, deleteStockImage, importStock, STOCK_TEMPLATE_URL } from '../../services/api';
import { usePartCatalog } from '../../hooks/usePartCatalog';
import { encodeHTML, checkTaxDocument } from '../../utils/security';
import TaxDocumentInput from '../../components/TaxDocumentInput';
import { msgStamp } from '../../utils/format';
import { useVehicles } from '../../hooks/useVehicles';
import ImageLightbox from '../../components/ImageLightbox';
import DeleteAccountCard from '../../components/DeleteAccountCard';
import { PART_CATEGORIES } from '../../models/partModel';

// "Tüm Kategoriler" = notified of every request; a specific category = only matching requests.
const CATS = ['Tüm Kategoriler', ...PART_CATEGORIES];

const COND_MAP   = { original: 'Orijinal (OEM)', aftermarket: 'Muadil', used: 'Çıkma / 2. El' };
const LEAD_OPTS  = [
  { val: 'same_day', label: 'Stokta Mevcut (Aynı Gün)' },
  { val: '1_2_days', label: '1-2 İş Günü' },
  { val: '3_5_days', label: '3-5 İş Günü' },
  { val: '1_week',   label: '1 Hafta+' },
];
const COND_OPTS  = [
  { val: 'original',    label: 'Orijinal (OEM)' },
  { val: 'aftermarket', label: 'Muadil' },
  { val: 'used',        label: 'Çıkma / 2. El' },
];
const STATUS_PILL = {
  pending:   'bg-yellow-50 text-yellow-700',
  quoted:    'bg-blue-50 text-blue-700',
  completed: 'bg-green-50 text-green-700',
  cancelled: 'bg-gray-50 text-gray-500',
};
const STATUS_LABEL = { pending:'Bekliyor', quoted:'Teklif Var', completed:'Tamamlandı', cancelled:'İptal' };

const INP = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:border-blue-600 transition';

const NAV = [
  { key: 'requests',  icon: '📋', label: 'Gelen Talepler', badge: true },
  { key: 'quotes',    icon: '💬', label: 'Tekliflerim'                 },
  { key: 'orders',    icon: '🧾', label: 'Siparişlerim'               },
  { key: 'messages',  icon: '✉️', label: 'Mesajlar'                    },
  { key: 'inventory', icon: '📦', label: 'Stok Yönetimi'              },
  { key: 'payment',   icon: '🏦', label: 'Ödeme Bilgileri'            },
  { key: 'profile',   icon: '🏢', label: 'Firma Profilim'             },
];

const ESCROW_PILL = {
  held:     { label: 'Ödendi · Teslimat Bekleniyor', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  released: { label: 'Ödeme Aktarıldı',              cls: 'bg-green-50 text-green-700 border-green-200'  },
  reversed: { label: 'İade Edildi',                  cls: 'bg-gray-100 text-gray-600 border-gray-200'    },
  none:     { label: 'Ödendi',                       cls: 'bg-green-50 text-green-700 border-green-200'  },
};

const SM_TYPES = [
  { val: 'PRIVATE_COMPANY',                label: 'Şahıs Şirketi' },
  { val: 'PERSONAL',                       label: 'Şahıs (Bireysel)' },
  { val: 'LIMITED_OR_JOINT_STOCK_COMPANY', label: 'Limited / Anonim Şirket' },
];

export default function SupplierDashboard() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const { brands: VEHICLE_BRANDS, models: VEHICLE_MODELS } = useVehicles();
  const partCatalog = usePartCatalog();

  const [nav,       setNav]       = useState('requests');
  const [pf,        setPf]        = useState({ firm_name: '', phone: '', city: '', category: '', tax_no: '', current_password: '', new_password: '' });
  const [pfSaving,  setPfSaving]  = useState(false);
  const [myRating,  setMyRating]  = useState(null);  // { average, count, reviews }
  const [sideOpen,  setSideOpen]  = useState(false);
  const [requests,  setRequests]  = useState([]);
  const [quotes,    setQuotes]    = useState([]);
  const [stock,     setStock]     = useState([]);
  const [convs,     setConvs]     = useState([]);
  const [activeConv,setActiveConv]= useState(null);
  const [messages,  setMessages]  = useState([]);
  const [msgText,   setMsgText]   = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [feedback,  setFeedback]  = useState('');

  // Filters
  const [filter,    setFilter]    = useState('all');
  const [search,    setSearch]    = useState('');

  // Quote modal
  const [modal,     setModal]     = useState(null);
  const [qForm,     setQForm]     = useState({ condition:'', price:'', vatIncluded:true, leadTime:'', notes:'', iban:'' });
  const [qImages,   setQImages]   = useState([]);   // File[] attached to the quote
  const [lightbox,  setLightbox]  = useState(null);  // enlarged image src
  const [qErr,      setQErr]      = useState('');
  const [sending,   setSending]   = useState(false);
  const [quotedSet, setQuotedSet] = useState(new Set());

  // Stock form
  const [stockModal, setStockModal] = useState(false);
  const EMPTY_STOCK = { name:'', oem_no:'', category:'', subcategory:'', car_brand:'', car_model:'', year_from:'', year_to:'', motor_code:'', gear_code:'', chassis_no:'', condition:'used', price:'', stock_qty:1, description:'' };
  const [stockForm,  setStockForm]  = useState(EMPTY_STOCK);
  const [stockPhotos, setStockPhotos] = useState([]);       // File[] for a new listing
  const [importing,  setImporting]  = useState(false);
  const [importResult, setImportResult] = useState(null);   // { created, errors, total }
  const [stockErr,   setStockErr]   = useState('');
  const [savingStock,setSavingStock]= useState(false);

  // Payment (iyzico sub-merchant) banking profile
  const [sm,       setSm]       = useState({ submerchant_type: 'PRIVATE_COMPANY', iban: '', name: '',
                                             email: '', gsm_number: '', address: '', identity_number: '',
                                             tax_number: '', tax_office: '', legal_company_title: '',
                                             contact_name: '', contact_surname: '' });
  const [smActive, setSmActive] = useState(false);
  const [smSaving, setSmSaving] = useState(false);

  // Orders on this supplier's quotes
  const [orders, setOrders] = useState([]);

  // Load this supplier's own rating + reviews.
  useEffect(() => {
    if (user?.id) getSupplierReviews(user.id).then(r => setMyRating(r.data)).catch(() => {});
  }, [user]);

  // Seed the firm-profile form from the current user.
  useEffect(() => {
    if (user) setPf(p => ({
      ...p,
      firm_name: user.firm_name || '',
      phone:     user.phone     || '',
      city:      user.city      || '',
      category:  user.category  || '',
      tax_no:    user.tax_no     || '',
    }));
  }, [user]);

  // ── Vergi levhası upload (required before payout details can be saved) ──
  const [taxDoc, setTaxDoc] = useState(null);
  const [taxUploading, setTaxUploading] = useState(false);
  const saveTaxDoc = async () => {
    setError('');
    const problem = checkTaxDocument(taxDoc);
    if (problem) return setError(problem);
    setTaxUploading(true);
    try {
      const res = await uploadTaxCertificate(taxDoc);
      updateUser(res.data.user);
      setTaxDoc(null);
      setFeedback('Vergi levhanız yüklendi. ✓');
      setTimeout(() => setFeedback(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Vergi levhası yüklenemedi.');
    } finally {
      setTaxUploading(false);
    }
  };

  const saveProfile = async () => {
    setError('');
    if (pf.new_password && !pf.current_password) {
      return setError('Şifre değiştirmek için mevcut şifrenizi girin.');
    }
    setPfSaving(true);
    const payload = {
      firm_name: pf.firm_name, phone: pf.phone, city: pf.city,
      category: pf.category, tax_no: pf.tax_no,
    };
    if (pf.new_password) {
      payload.current_password = pf.current_password;
      payload.new_password     = pf.new_password;
    }
    try {
      const res = await updateProfile(payload);
      updateUser(res.data);
      setPf(p => ({ ...p, current_password: '', new_password: '' }));
      setFeedback('Firma profiliniz güncellendi. ✓');
      setTimeout(() => setFeedback(''), 3000);
    } catch (err) {
      const errs  = err.response?.data?.errors;
      const first = errs ? Object.values(errs)[0] : null;
      setError(Array.isArray(first) ? first[0] : (first || 'Güncelleme başarısız.'));
    } finally {
      setPfSaving(false);
    }
  };

  const load = useCallback(async (section) => {
    setLoading(true);
    setError('');
    if (section === 'requests') {
      const res = await handleGetRequests();
      if (res.success) setRequests(res.data);
      else setError(res.error);
    }
    if (section === 'quotes') {
      const res = await handleGetQuotes();
      if (res.success) setQuotes(res.data);
      else setError(res.error);
    }
    if (section === 'messages') {
      try {
        const res = await getConversations();
        setConvs(res.data);
        if (res.data.length > 0 && !activeConv) setActiveConv(res.data[0]);
      } catch { setError('Mesajlar yüklenemedi.'); }
    }
    if (section === 'inventory') {
      try {
        const res = await getMyStock();    // own listings only (incl. inactive)
        setStock(res.data);
      } catch { setError('Stok yüklenemedi.'); }
    }
    if (section === 'orders') {
      const res = await handleGetOrders();
      if (res.success) setOrders(res.data);
      else setError(res.error);
    }
    if (section === 'payment') {
      const res = await handleGetSubMerchant();
      if (res.success && res.data?.exists) {
        setSmActive(!!res.data.is_active);
        setSm(s => ({
          ...s,
          submerchant_type:    res.data.submerchant_type    || s.submerchant_type,
          iban:                res.data.iban                || '',
          name:                res.data.name                || '',
          email:               res.data.email               || '',
          gsm_number:          res.data.gsm_number          || '',
          address:             res.data.address             || '',
          identity_number:     res.data.identity_number     || '',
          tax_number:          res.data.tax_number          || '',
          tax_office:          res.data.tax_office          || '',
          legal_company_title: res.data.legal_company_title || '',
          contact_name:        res.data.contact_name        || '',
          contact_surname:     res.data.contact_surname     || '',
        }));
      } else {
        // Prefill sensible defaults from the firm profile on first setup.
        setSmActive(false);
        setSm(s => ({ ...s, name: s.name || user?.firm_name || '',
                      email: s.email || user?.email || '',
                      gsm_number: s.gsm_number || user?.phone || '',
                      address: s.address || user?.city || '' }));
      }
    }
    setLoading(false);
  }, [activeConv, user]);

  const saveSubMerchant = async () => {
    setError('');
    setSmSaving(true);
    const res = await handleSaveSubMerchant(sm);
    setSmSaving(false);
    if (res.success) {
      if (res.data?.is_active) setSmActive(true);
      setFeedback(res.data?.message || 'Ödeme bilgileriniz kaydedildi. ✓');
      setTimeout(() => setFeedback(''), 4000);
    } else setError(res.error);
  };

  useEffect(() => { load(nav); }, [nav]);

  useEffect(() => {
    if (!activeConv) return;
    getMessages(activeConv.id).then(r => setMessages(r.data)).catch(() => {});
  }, [activeConv]);

  const filtered = requests.filter(r => {
    const isQ = quotedSet.has(r.id);
    const st  = isQ ? 'quoted' : r.status;
    const mf  = filter === 'all' || st === filter;
    const ms  = !search || r.part_name?.toLowerCase().includes(search.toLowerCase())
                        || (`${r.brand} ${r.model}`).toLowerCase().includes(search.toLowerCase());
    return mf && ms;
  });

  const sendQuote = async () => {
    setQErr('');
    if (!qForm.condition) { setQErr('Parça durumu seçilmelidir.'); return; }
    const price = parseFloat(qForm.price);
    if (!price || price <= 0) { setQErr('Geçerli bir fiyat girin.'); return; }
    if (!qForm.leadTime) { setQErr('Temin süresi seçilmelidir.'); return; }

    setSending(true);
    const res = await handleCreateQuote({
      request:      modal.id,
      condition:    qForm.condition,
      price,
      vatIncluded:  qForm.vatIncluded,
      leadTime:     qForm.leadTime,
      notes:        qForm.notes,
      iban:         qForm.iban,
      images:       qImages,
    });
    setSending(false);
    if (res.error) { setQErr(res.error); return; }
    setQuotedSet(p => new Set([...p, modal.id]));
    setModal(null);
    setQForm({ condition:'', price:'', vatIncluded:true, leadTime:'', notes:'', iban:'' });
    setQImages([]);
    setFeedback('Teklif başarıyla gönderildi! ✓');
    setTimeout(() => setFeedback(''), 3000);
  };

  const sendMsg = async () => {
    const clean = msgText.replace(/<[^>]*>/g, '').trim().slice(0, 1000);
    if (!clean || !activeConv) return;
    try {
      const res = await apiSendMessage(activeConv.id, { text: clean });
      setMessages(p => [...p, res.data]);
      setMsgText('');
    } catch { setError('Mesaj gönderilemedi.'); }
  };

  const startChat = async (requestId) => {
    try {
      const res = await startConversation({ request_id: requestId, supplier_id: user.id });
      setNav('messages');
      await load('messages');
      setActiveConv(res.data);
    } catch { setError('Konuşma başlatılamadı.'); }
  };

  const askAdminHelp = async () => {
    if (!activeConv) return;
    try {
      await requestAdminHelp({ conversation_id: activeConv.id });
      setFeedback('Admin desteği talebiniz iletildi. ✓');
      setTimeout(() => setFeedback(''), 4000);
      getMessages(activeConv.id).then(r => setMessages(r.data)).catch(() => {});
    } catch { setError('Destek talebi gönderilemedi.'); }
  };

  const saveStock = async () => {
    setStockErr('');
    if (!stockForm.name)     { setStockErr('Parça adı zorunludur.'); return; }
    if (!stockForm.category) { setStockErr('Kategori zorunludur.'); return; }
    const price = parseFloat(stockForm.price);
    if (!price || price <= 0) { setStockErr('Geçerli bir fiyat girin.'); return; }

    setSavingStock(true);
    try {
      const fd = new FormData();
      Object.entries({ ...stockForm, price }).forEach(([k, v]) => fd.append(k, v ?? ''));
      stockPhotos.slice(0, 5).forEach(f => fd.append('images', f));
      await createStock(fd);
      setStockModal(false);
      setStockForm(EMPTY_STOCK);
      setStockPhotos([]);
      load('inventory');
      setFeedback('Stok parça eklendi! ✓');
      setTimeout(() => setFeedback(''), 3000);
    } catch (err) {
      const errs = err.response?.data?.errors;
      const first = errs ? Object.values(errs)[0] : null;
      setStockErr((Array.isArray(first) ? first[0] : first) || 'Stok eklenemedi.');
    }
    setSavingStock(false);
  };

  // Add photos to an existing listing / remove one.
  const addPhotos = async (s, fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/')).slice(0, 5 - (s.images?.length || 0));
    if (!files.length) return;
    try {
      const res = await addStockImages(s.id, files);
      setStock(p => p.map(x => x.id === s.id ? res.data : x));
    } catch (err) {
      setError(err.response?.data?.message || 'Fotoğraf eklenemedi.');
    }
  };
  const removePhoto = async (s, idx) => {
    try {
      await deleteStockImage(s.image_ids[idx]);
      setStock(p => p.map(x => x.id === s.id
        ? { ...x, images: x.images.filter((_, i) => i !== idx), image_ids: x.image_ids.filter((_, i) => i !== idx) } : x));
    } catch { setError('Fotoğraf silinemedi.'); }
  };

  // Bulk upload from Excel / CSV.
  const runImport = async (file) => {
    if (!file) return;
    setImporting(true); setImportResult(null); setError('');
    try {
      const res = await importStock(file);
      setImportResult(res.data);
      load('inventory');
    } catch (err) {
      const d = err.response?.data;
      if (d && Array.isArray(d.errors)) setImportResult(d);
      else setError(d?.message || 'Dosya yüklenemedi.');
    } finally {
      setImporting(false);
    }
  };

  const delStock = async (id) => {
    if (!window.confirm('Bu parçayı silmek istediğinizden emin misiniz?')) return;
    try {
      await deleteStock(id);
      setStock(p => p.filter(s => s.id !== id));
    } catch { setError('Silme işlemi başarısız.'); }
  };

  const newCount = requests.filter(r => r.status === 'pending' && !quotedSet.has(r.id)).length;

  return (
    <div className="flex min-h-screen bg-gray-100">
      {sideOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setSideOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`w-56 bg-gray-900 flex flex-col fixed top-0 bottom-0 z-40 transition-transform duration-300
        ${sideOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="px-5 py-5 border-b border-white/10">
          <div className="text-white text-lg font-bold cursor-pointer" onClick={() => navigate('/')}>
            Oto<span className="text-yellow-400">ParçamıBul</span>
          </div>
          <div className="text-white/30 text-xs mt-1">Tedarikçi Paneli</div>
        </div>
        <nav className="flex-1 py-3">
          {NAV.map(n => (
            <button key={n.key} onClick={() => { setNav(n.key); setSideOpen(false); }}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition border-l-4
                ${nav === n.key ? 'bg-blue-900/30 text-white border-blue-500' : 'text-white/50 border-transparent hover:bg-white/5 hover:text-white'}`}>
              <span>{n.icon}</span>{n.label}
              {n.badge && newCount > 0 && (
                <span className="ml-auto bg-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{newCount}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-700 text-white text-xs font-bold flex items-center justify-center">
              {user?.firm_name?.[0] || 'T'}
            </div>
            <div className="min-w-0">
              <div className="text-white text-xs font-medium truncate">{user?.firm_name || 'Tedarikçi'}</div>
              <div className="text-white/30 text-xs truncate">{user?.email || ''}</div>
            </div>
          </div>
          <button onClick={() => handleLogout(logout, navigate)}
            className="w-full text-xs border border-white/20 text-white/50 py-1.5 rounded-lg hover:border-white/40 hover:text-white transition">
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="md:ml-56 flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 h-14 flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-gray-500 text-xl" onClick={() => setSideOpen(o => !o)}>☰</button>
            <h1 className="text-base font-semibold text-gray-800">{NAV.find(n => n.key === nav)?.label}</h1>
          </div>
          <span className="bg-green-50 text-green-700 border border-green-200 text-xs font-semibold px-3 py-1 rounded-full">🟢 Aktif</span>
        </header>

        <div className="p-4 md:p-6 flex-1">
          {feedback && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">{feedback}</div>
          )}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm flex justify-between">
              <span>{error}</span>
              <button onClick={() => setError('')} className="ml-4 text-red-400">✕</button>
            </div>
          )}
          {user?.tax_certificate_missing && nav !== 'profile' && (
            <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="flex-1">
                📄 <strong>Vergi levhanızı yükleyin.</strong> Ödeme alabilmeniz için vergi levhanızın sistemde olması gerekir.
              </span>
              <button onClick={() => setNav('profile')}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition whitespace-nowrap">
                Şimdi Yükle
              </button>
            </div>
          )}

          {/* ── Requests ── */}
          {nav === 'requests' && (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                {[
                  { label: 'Yeni Talepler',    val: newCount },
                  { label: 'Toplam Talepler',  val: requests.length },
                  { label: 'Verilen Teklifler',val: quotedSet.size  },
                  { label: 'Tamamlanan',        val: requests.filter(r => r.status === 'completed').length },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{s.label}</div>
                    <div className="text-2xl font-bold text-gray-800">{s.val}</div>
                  </div>
                ))}
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2 mb-4 items-center">
                {[['all','Tümü'],['pending','Yeni'],['quoted','Teklif Verildi'],['completed','Tamamlandı']].map(([v, l]) => (
                  <button key={v} onClick={() => setFilter(v)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition
                      ${filter === v ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400'}`}>
                    {l}
                    {v === 'pending' && newCount > 0 && (
                      <span className="ml-1.5 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full">{newCount}</span>
                    )}
                  </button>
                ))}
                <input type="text"
                  className="ml-auto px-4 py-1.5 border border-gray-200 rounded-full text-xs w-44 focus:outline-none focus:border-blue-400"
                  placeholder="🔍 Talep ara..."
                  value={search}
                  onChange={e => setSearch(e.target.value.replace(/<[^>]*>/g, ''))}
                />
              </div>

              {loading && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}
              {!loading && filtered.length === 0 && (
                <p className="text-center text-gray-400 py-10 text-sm">Talep bulunamadı.</p>
              )}
              <div className="space-y-3">
                {filtered.map(r => {
                  const isQ = quotedSet.has(r.id);
                  const st  = isQ ? 'quoted' : r.status;
                  return (
                    <div key={r.id}
                      className={`bg-white rounded-xl border border-gray-100 border-l-4 p-4 shadow-sm hover:shadow-md transition
                        ${st === 'pending' ? 'border-l-red-500' : st === 'quoted' ? 'border-l-yellow-400' : 'border-l-green-500'}`}>
                      {/* Header: meta + status */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
                          <span className="font-mono">#{r.id}</span>
                          <span>🕐 {new Date(r.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          {r.quote_count > 0 && <span>💬 {r.quote_count} teklif</span>}
                        </div>
                        <span className={`flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_PILL[st]}`}>
                          {STATUS_LABEL[st]}
                        </span>
                      </div>

                      {/* Part + Vehicle detail side by side */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                        <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100">
                          <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider mb-1">🔧 Aranan Parça</p>
                          <p className="text-sm font-bold text-gray-800 leading-snug">{r.part_name}</p>
                          {r.subcategory && <p className="text-xs text-gray-500 mt-0.5">{r.subcategory}</p>}
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">🚗 Araç</p>
                          <p className="text-sm font-bold text-gray-800 leading-snug">
                            {r.brand} {r.model} <span className="font-normal text-gray-500">· {r.year}</span>
                          </p>
                          {(r.engine || r.fuel || r.gear) && (
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {r.engine && <span className="text-[11px] bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{r.engine}</span>}
                              {r.fuel   && <span className="text-[11px] bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{r.fuel}</span>}
                              {r.gear   && <span className="text-[11px] bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{r.gear}</span>}
                            </div>
                          )}
                          {r.chassis_no && (
                            <p className="text-[11px] text-gray-500 mt-1.5 font-mono">
                              🔖 Şasi: <span className="text-gray-700">{r.chassis_no}</span>
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Customer note */}
                      {r.description && (
                        <div className="text-xs text-gray-600 bg-amber-50/60 rounded-lg px-3 py-2 mb-3 border border-amber-100">
                          <span className="font-semibold text-gray-500">Not: </span>{r.description}
                        </div>
                      )}

                      {/* Customer photos */}
                      {r.images && r.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {r.images.map((src, i) => (
                            <button key={i} type="button" onClick={() => setLightbox(src)}
                              className="block w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 transition">
                              <img src={src} alt="Parça fotoğrafı" className="w-full h-full object-cover" loading="lazy" />
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2 flex-wrap">
                        {st === 'completed' ? (
                          <span className="bg-green-50 text-green-700 border border-green-200 text-xs font-semibold px-3 py-1.5 rounded-lg">✓ Tamamlandı</span>
                        ) : isQ ? (
                          <span className="bg-green-50 text-green-700 border border-green-200 text-xs font-semibold px-3 py-1.5 rounded-lg">✓ Teklif Gönderildi</span>
                        ) : (
                          <button onClick={() => setModal({ id: r.id, title: r.part_name })}
                            className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold px-4 py-1.5 rounded-lg transition">
                            Teklif Ver
                          </button>
                        )}
                        <button onClick={() => startChat(r.id)}
                          className="border border-blue-200 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-blue-50 transition">
                          💬 Mesaj
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── My Quotes ── */}
          {nav === 'quotes' && (
            <div>
              {loading && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}
              {!loading && quotes.length === 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                  <div className="text-4xl mb-3">💬</div>
                  <p className="text-sm text-gray-500">Henüz teklif vermediniz.</p>
                </div>
              )}
              <div className="space-y-3">
                {quotes.map(q => (
                  <div key={q.id} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                    <div className="flex justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-xs text-gray-400">{q.request_info}</p>
                        <p className="text-sm font-semibold text-gray-800">₺{parseFloat(q.price).toLocaleString('tr-TR')}</p>
                        <p className="text-xs text-gray-400">{COND_MAP[q.condition]} · {q.vat_included ? 'KDV Dahil' : 'KDV Hariç'}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full self-start
                        ${q.status === 'sent' ? 'bg-yellow-50 text-yellow-700'
                          : q.status === 'accepted' ? 'bg-green-50 text-green-700'
                          : 'bg-red-50 text-red-600'}`}>
                        {q.status === 'sent' ? 'Gönderildi' : q.status === 'accepted' ? '✓ Kabul Edildi' : '✕ Reddedildi'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Messages ── */}
          {nav === 'messages' && (
            <div className="flex flex-col md:flex-row gap-4 h-[70vh]">
              <div className="w-full md:w-56 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-y-auto flex-shrink-0">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Konuşmalar</p>
                </div>
                {convs.length === 0 && <p className="text-xs text-gray-400 text-center py-6">Henüz mesajınız yok</p>}
                {convs.map(c => (
                  <button key={c.id} onClick={() => setActiveConv(c)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition ${activeConv?.id === c.id ? 'bg-blue-50' : ''}`}>
                    <p className="text-xs font-semibold text-gray-800 truncate">{c.other_party?.name || 'Müşteri'}</p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{c.request_summary}</p>
                    {c.unread_count > 0 && (
                      <span className="mt-1 inline-block bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">{c.unread_count}</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex-1 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">
                {!activeConv ? (
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Bir konuşma seçin</div>
                ) : (
                  <>
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{activeConv.other_party?.name}</p>
                        <p className="text-xs text-gray-400 truncate">{activeConv.request_summary}</p>
                      </div>
                      <button onClick={askAdminHelp} title="Sorun mu var? Admin desteği isteyin"
                        className="flex-shrink-0 text-xs bg-red-50 text-red-700 border border-red-200 font-semibold px-3 py-1.5 rounded-lg hover:bg-red-100 transition">
                        🆘 Admin Desteği
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-5 space-y-3">
                      {messages.map(m => (
                        <div key={m.id} className={`flex ${m.is_mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs md:max-w-sm px-4 py-2.5 rounded-2xl text-sm
                            ${m.is_mine ? 'bg-blue-700 text-white rounded-br-none' : 'bg-gray-100 text-gray-800 rounded-bl-none'}`}>
                            <p dangerouslySetInnerHTML={{ __html: encodeHTML(m.text) }} />
                            <p className={`text-xs mt-1 ${m.is_mine ? 'text-blue-200' : 'text-gray-400'}`}>
                              {msgStamp(m.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-4 border-t border-gray-100 flex gap-3">
                      <input type="text"
                        className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-600 transition"
                        placeholder="Mesajınızı yazın..."
                        value={msgText} maxLength={1000}
                        onChange={e => setMsgText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && sendMsg()}
                      />
                      <button onClick={sendMsg}
                        className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition">
                        Gönder
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Inventory ── */}
          {nav === 'inventory' && (
            <div>
              <div className="flex justify-between items-center mb-5 gap-3 flex-wrap">
                <p className="text-sm text-gray-500">{stock.length} parça stokta</p>
                <div className="flex gap-2">
                  <label className={`cursor-pointer border border-blue-200 text-blue-700 bg-white hover:bg-blue-50 text-xs font-semibold px-4 py-2 rounded-lg transition ${importing ? 'opacity-60 pointer-events-none' : ''}`}>
                    {importing ? 'Yükleniyor…' : '📥 Excel / CSV ile Toplu Yükle'}
                    <input type="file" accept=".xlsx,.csv" className="hidden"
                      onChange={e => { runImport(e.target.files?.[0]); e.target.value = ''; }} />
                  </label>
                  <button onClick={() => setStockModal(true)}
                    className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold px-4 py-2 rounded-lg transition">
                    + Parça Ekle
                  </button>
                </div>
              </div>

              {/* Bulk upload help + result */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-5 text-xs text-blue-800">
                Stoğunuzu tek seferde yükleyin: <a href={STOCK_TEMPLATE_URL} className="font-semibold underline">Excel şablonunu indirin</a>,
                {' '}doldurun ve yükleyin. En az <strong>Parça Adı</strong> ve <strong>Fiyat</strong> gerekir; kategori boşsa parça adından bulunur.
                {' '}Fotoğrafları yükledikten sonra her ilana ayrıca ekleyebilirsiniz.
              </div>
              {importResult && (
                <div className={`rounded-xl px-4 py-3 mb-5 text-sm border ${importResult.created ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-100 text-red-700'}`}>
                  <div className="flex justify-between gap-3">
                    <strong>{importResult.created} / {importResult.total} satır eklendi.</strong>
                    <button onClick={() => setImportResult(null)} className="text-xs opacity-60">✕</button>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <ul className="mt-2 text-xs space-y-0.5 max-h-40 overflow-y-auto">
                      {importResult.errors.map(e => <li key={e.row}>Satır {e.row}: {e.message}</li>)}
                    </ul>
                  )}
                </div>
              )}
              {loading && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}
              {!loading && stock.length === 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                  <div className="text-4xl mb-3">📦</div>
                  <p className="text-sm text-gray-500">Henüz stok parça eklemediniz.</p>
                </div>
              )}
              <div className="space-y-3">
                {stock.map(s => (
                  <div key={s.id} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm flex justify-between items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex gap-1.5 flex-shrink-0">
                        {(s.images || []).map((src, i) => (
                          <div key={src} className="relative group">
                            <img src={src} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-200" />
                            <button onClick={() => removePhoto(s, i)} title="Fotoğrafı sil"
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white text-[10px] hidden group-hover:flex items-center justify-center">✕</button>
                          </div>
                        ))}
                        {(s.images?.length || 0) < 5 && (
                          <label title="Fotoğraf ekle"
                            className="w-12 h-12 rounded-lg border-2 border-dashed border-gray-200 hover:border-blue-400 flex items-center justify-center text-gray-400 cursor-pointer text-lg">
                            +
                            <input type="file" accept="image/*" multiple className="hidden"
                              onChange={e => { addPhotos(s, e.target.files); e.target.value = ''; }} />
                          </label>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{s.name}</p>
                        <p className="text-xs text-gray-400">{[s.category, s.subcategory].filter(Boolean).join(' › ')} · {COND_MAP[s.condition]}</p>
                        <p className="text-xs text-gray-400">
                          {[[s.car_brand, s.car_model].filter(Boolean).join(' '), s.oem_no && `OEM: ${s.oem_no}`].filter(Boolean).join(' · ')}
                          {!s.images?.length && <span className="text-amber-600"> · Fotoğraflı ilanlar daha çok ilgi görür</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">₺{parseFloat(s.price).toLocaleString('tr-TR')}</p>
                        <p className="text-xs text-gray-400">Stok: {s.stock_qty}</p>
                      </div>
                      <button onClick={() => delStock(s.id)}
                        className="text-xs text-red-500 hover:text-red-700 border border-red-200 px-2 py-1 rounded-lg transition">
                        Sil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Orders (Siparişlerim) ── */}
          {nav === 'orders' && (
            <div>
              {loading && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}
              {!loading && orders.length === 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                  <div className="text-4xl mb-3">🧾</div>
                  <p className="text-sm text-gray-500">Henüz siparişiniz yok.</p>
                  <p className="text-xs text-gray-400 mt-1">Kabul edilen ve ödenen teklifler burada görünür.</p>
                </div>
              )}
              <div className="space-y-4">
                {orders.map(o => {
                  const esc = ESCROW_PILL[o.escrow_status] || ESCROW_PILL.none;
                  const refunded = o.status === 'refunded';
                  return (
                    <div key={o.id}
                      className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-gray-800">Sipariş #{o.id}</span>
                          <span className="text-xs text-gray-400">Alıcı: {o.buyer_name}</span>
                          {refunded ? (
                            <span className="text-xs border px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border-gray-200">İade Edildi</span>
                          ) : (
                            <span className={`text-xs border px-2 py-0.5 rounded-full ${esc.cls}`}>{esc.label}</span>
                          )}
                          {o.refund && o.refund.status === 'requested' && (
                            <span className="text-xs border px-2 py-0.5 rounded-full bg-red-50 text-red-600 border-red-200">İade Talebi Var</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{o.part_info}</p>
                        <p className="text-xs text-gray-400 mt-1">{new Date(o.created_at).toLocaleDateString('tr-TR')}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg font-bold text-gray-900">₺{parseFloat(o.supplier_amount || o.price).toLocaleString('tr-TR')}</div>
                        <div className="text-xs text-gray-400">
                          Toplam ₺{parseFloat(o.price).toLocaleString('tr-TR')} · komisyon ₺{parseFloat(o.commission_amount || 0).toLocaleString('tr-TR')}
                        </div>
                        {o.escrow_status === 'held' && !refunded && (
                          <div className="text-xs text-amber-600 mt-1">Teslimat sonrası aktarılır</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Payment / banking (iyzico sub-merchant) ── */}
          {nav === 'payment' && (
            <div className="max-w-lg space-y-5">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-7">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-bold text-gray-800">Ödeme / Banka Bilgileri</h3>
                  <span className={`text-xs font-semibold border px-2.5 py-1 rounded-full ${smActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>
                    {smActive ? '✓ Aktif' : '⏳ Tamamlanmadı'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed mb-5">
                  Ödemeler iyzico üzerinden alınır ve size ait tutar (komisyon düşülerek) doğrudan
                  IBAN'ınıza aktarılır. Ödeme alabilmeniz için bu bilgileri eksiksiz doldurmanız gerekir.
                </p>

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">İşyeri Tipi</label>
                <select className={INP + ' mb-4'} value={sm.submerchant_type}
                  onChange={e => setSm(s => ({ ...s, submerchant_type: e.target.value }))}>
                  {SM_TYPES.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                </select>

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">İşyeri / Ad</label>
                <input type="text" maxLength={150} className={INP + ' mb-4'} value={sm.name}
                  onChange={e => setSm(s => ({ ...s, name: e.target.value }))} />

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">IBAN</label>
                <input type="text" maxLength={34} placeholder="TR.. .. .. .." className={INP + ' mb-4 font-mono'}
                  value={sm.iban}
                  onChange={e => setSm(s => ({ ...s, iban: e.target.value.toUpperCase() }))} />

                {sm.submerchant_type === 'PERSONAL' && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Ad</label>
                        <input type="text" maxLength={100} className={INP + ' mb-4'} value={sm.contact_name}
                          onChange={e => setSm(s => ({ ...s, contact_name: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Soyad</label>
                        <input type="text" maxLength={100} className={INP + ' mb-4'} value={sm.contact_surname}
                          onChange={e => setSm(s => ({ ...s, contact_surname: e.target.value }))} />
                      </div>
                    </div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">TC Kimlik No</label>
                    <input type="text" maxLength={11} inputMode="numeric" className={INP + ' mb-4'} value={sm.identity_number}
                      onChange={e => setSm(s => ({ ...s, identity_number: e.target.value.replace(/\D/g, '').slice(0, 11) }))} />
                  </>
                )}

                {sm.submerchant_type === 'PRIVATE_COMPANY' && (
                  <>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">TC Kimlik No (Sahip)</label>
                    <input type="text" maxLength={11} inputMode="numeric" className={INP + ' mb-4'} value={sm.identity_number}
                      onChange={e => setSm(s => ({ ...s, identity_number: e.target.value.replace(/\D/g, '').slice(0, 11) }))} />
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Yasal Ünvan</label>
                    <input type="text" maxLength={200} className={INP + ' mb-4'} value={sm.legal_company_title}
                      onChange={e => setSm(s => ({ ...s, legal_company_title: e.target.value }))} />
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Vergi Dairesi</label>
                    <input type="text" maxLength={100} className={INP + ' mb-4'} value={sm.tax_office}
                      onChange={e => setSm(s => ({ ...s, tax_office: e.target.value }))} />
                  </>
                )}

                {sm.submerchant_type === 'LIMITED_OR_JOINT_STOCK_COMPANY' && (
                  <>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Yasal Ünvan</label>
                    <input type="text" maxLength={200} className={INP + ' mb-4'} value={sm.legal_company_title}
                      onChange={e => setSm(s => ({ ...s, legal_company_title: e.target.value }))} />
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Vergi No (VKN)</label>
                        <input type="text" maxLength={10} inputMode="numeric" className={INP + ' mb-4'} value={sm.tax_number}
                          onChange={e => setSm(s => ({ ...s, tax_number: e.target.value.replace(/\D/g, '').slice(0, 10) }))} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Vergi Dairesi</label>
                        <input type="text" maxLength={100} className={INP + ' mb-4'} value={sm.tax_office}
                          onChange={e => setSm(s => ({ ...s, tax_office: e.target.value }))} />
                      </div>
                    </div>
                  </>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">E-posta</label>
                    <input type="email" maxLength={254} className={INP + ' mb-4'} value={sm.email}
                      onChange={e => setSm(s => ({ ...s, email: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Telefon</label>
                    <input type="tel" maxLength={20} className={INP + ' mb-4'} value={sm.gsm_number}
                      onChange={e => setSm(s => ({ ...s, gsm_number: e.target.value }))} />
                  </div>
                </div>

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Adres</label>
                <textarea rows={2} maxLength={255} className={INP} value={sm.address}
                  onChange={e => setSm(s => ({ ...s, address: e.target.value }))} />
              </div>

              <button onClick={saveSubMerchant} disabled={smSaving}
                className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl text-sm transition">
                {smSaving ? 'Kaydediliyor...' : 'Ödeme Bilgilerini Kaydet'}
              </button>
            </div>
          )}

          {/* ── Profile ── */}
          {nav === 'profile' && (
            <div className="max-w-lg space-y-5">
              {/* Editable firm profile */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-7">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-bold text-gray-800">Firma Bilgileri</h3>
                  <span className={`text-xs font-semibold border px-2.5 py-1 rounded-full ${user?.is_approved ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>
                    {user?.is_approved ? '✓ Onaylı' : '⏳ Onay Bekliyor'}
                  </span>
                </div>
                <div className="mb-5 text-sm">
                  {myRating && myRating.count > 0 ? (
                    <span className="text-amber-600 font-semibold">★ {myRating.average}
                      <span className="text-gray-400 font-normal"> — {myRating.count} değerlendirme</span>
                    </span>
                  ) : (
                    <span className="text-gray-400">Henüz değerlendirme yok</span>
                  )}
                </div>

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Firma Adı</label>
                <input type="text" maxLength={150}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-4 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                  value={pf.firm_name}
                  onChange={e => setPf(p => ({ ...p, firm_name: e.target.value }))} />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Telefon</label>
                    <input type="tel" maxLength={15} placeholder="05xx xxx xx xx"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-4 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                      value={pf.phone}
                      onChange={e => setPf(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Şehir</label>
                    <input type="text" maxLength={80}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-4 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                      value={pf.city}
                      onChange={e => setPf(p => ({ ...p, city: e.target.value }))} />
                  </div>
                </div>

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Uzmanlık Kategorisi</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-1 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                  value={pf.category}
                  onChange={e => setPf(p => ({ ...p, category: e.target.value }))}>
                  <option value="">Seçin</option>
                  {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <p className="text-xs text-gray-400 mb-4">"Tüm Kategoriler" seçerseniz her talep için e-posta alırsınız; belirli bir kategori seçerseniz yalnızca o kategorideki talepler için bildirim alırsınız.</p>

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Vergi / T.C. No</label>
                <input type="text" maxLength={11} inputMode="numeric" disabled={!!user?.is_approved && !!user?.tax_no}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-1 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition disabled:bg-gray-50 disabled:text-gray-500"
                  value={pf.tax_no}
                  onChange={e => setPf(p => ({ ...p, tax_no: e.target.value.replace(/\D/g, '').slice(0, 11) }))} />
                <p className="text-xs text-gray-400 mb-4">
                  {user?.is_approved && user?.tax_no
                    ? 'Onaylı hesaplarda vergi numarası değişikliği için info@otoparcamibul.com ile iletişime geçin.'
                    : 'Şirketler için 10 haneli vergi numarası, şahıs işletmeleri için T.C. kimlik numarası.'}
                </p>

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">E-posta</label>
                <input type="email" disabled value={user?.email || ''}
                  className="w-full px-3 py-2.5 border border-gray-100 bg-gray-50 text-gray-400 rounded-lg text-sm mb-1 cursor-not-allowed" />
                <p className="text-xs text-gray-400">E-posta adresi değiştirilemez.</p>
              </div>

              {/* Vergi levhası */}
              <div className={`bg-white rounded-2xl border shadow-sm p-7 ${user?.tax_certificate_missing ? 'border-amber-300' : 'border-gray-100'}`}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-base font-bold text-gray-800">Vergi Levhası</h3>
                  {user?.tax_verified ? (
                    <span className="text-xs font-semibold border px-2.5 py-1 rounded-full bg-green-50 text-green-700 border-green-200">
                      ✓ Doğrulandı
                    </span>
                  ) : user?.has_tax_certificate ? (
                    <span className="text-xs font-semibold border px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border-blue-200">
                      Yüklendi · kontrol bekleniyor
                    </span>
                  ) : (
                    <span className="text-xs font-semibold border px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border-amber-200">Eksik</span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  {user?.tax_verified
                    ? 'Vergi levhanız kontrol edildi: tekliflerinizde alıcılara "Doğrulanmış satıcı" rozeti gösterilir. Yeni belge yüklerseniz rozet, tekrar kontrol edilene kadar kaldırılır.'
                    : user?.has_tax_certificate
                    ? 'Belgeniz kontrol edildikten sonra tekliflerinizde "Doğrulanmış satıcı" rozeti görünür.'
                    : 'Ödeme alabilmeniz için güncel vergi levhanızı yükleyin. Belge yalnızca doğrulama amacıyla yönetici tarafından görüntülenir.'}
                </p>
                <TaxDocumentInput file={taxDoc} onChange={setTaxDoc} accent="blue" />
                <button onClick={saveTaxDoc} disabled={!taxDoc || taxUploading}
                  className="w-full mt-4 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300 text-white font-semibold py-2.5 rounded-xl text-sm transition">
                  {taxUploading ? 'Yükleniyor…' : user?.has_tax_certificate ? 'Belgeyi Değiştir' : 'Vergi Levhasını Yükle'}
                </button>
              </div>

              {/* Password change */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-7">
                <h3 className="text-base font-bold text-gray-800 mb-1">Şifre Değiştir</h3>
                <p className="text-xs text-gray-400 mb-5">Değiştirmek istemiyorsanız boş bırakın.</p>

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Mevcut Şifre</label>
                <input type="password" maxLength={128} autoComplete="current-password"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm mb-4 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                  value={pf.current_password}
                  onChange={e => setPf(p => ({ ...p, current_password: e.target.value }))} />

                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Yeni Şifre</label>
                <input type="password" maxLength={128} autoComplete="new-password" placeholder="Min. 8 karakter, 1 büyük harf, 1 rakam"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                  value={pf.new_password}
                  onChange={e => setPf(p => ({ ...p, new_password: e.target.value }))} />
              </div>

              <button onClick={saveProfile} disabled={pfSaving}
                className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl text-sm transition">
                {pfSaving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </button>

              <DeleteAccountCard isSupplier />
            </div>
          )}
        </div>
      </main>

      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />

      {/* Quote modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-7 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-800 mb-1">Teklif Gönder</h3>
            <p className="text-sm text-gray-400 mb-5">#{modal.id} — {modal.title}</p>
            {qErr && <div className="bg-red-50 border border-red-100 text-red-600 rounded-lg px-3 py-2 text-sm mb-4">⚠️ {qErr}</div>}
            <div className="space-y-3">
              <select className={INP} value={qForm.condition} onChange={e => setQForm(p => ({ ...p, condition: e.target.value }))}>
                <option value="">Parça Durumu Seçin</option>
                {COND_OPTS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" className={INP} placeholder="Fiyat (₺)"
                  value={qForm.price} onChange={e => setQForm(p => ({ ...p, price: e.target.value }))} />
                <select className={INP} value={qForm.vatIncluded}
                  onChange={e => setQForm(p => ({ ...p, vatIncluded: e.target.value === 'true' }))}>
                  <option value="true">KDV Dahil</option>
                  <option value="false">KDV Hariç</option>
                </select>
              </div>

              <select className={INP} value={qForm.leadTime} onChange={e => setQForm(p => ({ ...p, leadTime: e.target.value }))}>
                <option value="">Temin Süresi</option>
                {LEAD_OPTS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
              <textarea className={`${INP} min-h-[70px] resize-y`} placeholder="Garanti, marka bilgisi (isteğe bağlı)"
                value={qForm.notes} maxLength={500} onChange={e => setQForm(p => ({ ...p, notes: e.target.value }))} />

              {/* IBAN (optional) — shown to the customer for payment */}
              <div>
                <input className={`${INP} font-mono uppercase`} placeholder="IBAN (isteğe bağlı)" maxLength={34}
                  value={qForm.iban}
                  onChange={e => setQForm(p => ({ ...p, iban: e.target.value.toUpperCase() }))} />
                <p className="text-[11px] text-gray-400 mt-1 px-1">Girerseniz müşteri, ödemeyi yapabilmesi için bu IBAN'ı görür. TR ile başlamalıdır.</p>
              </div>

              {/* Photo upload (optional, max 5) */}
              <div>
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-3 text-sm text-gray-500 cursor-pointer hover:border-blue-400 hover:text-blue-600 transition">
                  📷 Fotoğraf Ekle <span className="text-xs text-gray-400">(parça görseli, isteğe bağlı)</span>
                  <input type="file" accept="image/*" multiple className="hidden"
                    onChange={e => {
                      const picked = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
                      setQImages(prev => [...prev, ...picked].slice(0, 5));
                      e.target.value = '';
                    }} />
                </label>
                {qImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {qImages.map((f, i) => (
                      <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                        <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                        <button type="button"
                          onClick={() => setQImages(prev => prev.filter((_, idx) => idx !== i))}
                          className="absolute top-0 right-0 bg-black/60 text-white w-5 h-5 text-xs leading-none rounded-bl-lg">×</button>
                      </div>
                    ))}
                  </div>
                )}
                {qImages.length >= 5 && <p className="text-xs text-gray-400 mt-1">En fazla 5 fotoğraf.</p>}
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setModal(null); setQErr(''); setQImages([]); }}
                className="flex-1 border border-gray-200 text-gray-500 py-2.5 rounded-xl text-sm">İptal</button>
              <button onClick={sendQuote} disabled={sending}
                className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-2.5 rounded-xl text-sm transition">
                {sending ? 'Gönderiliyor...' : '✉️ Teklifi Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock modal */}
      {stockModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-7 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-5">Stok Parça Ekle</h3>
            {stockErr && <div className="bg-red-50 border border-red-100 text-red-600 rounded-lg px-3 py-2 text-sm mb-4">⚠️ {stockErr}</div>}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <select className={INP} value={stockForm.category}
                  onChange={e => setStockForm(p => ({ ...p, category: e.target.value, subcategory: '' }))}>
                  <option value="">Kategori *</option>
                  {partCatalog.categories.map(c => <option key={c}>{c}</option>)}
                </select>
                <select className={INP} value={stockForm.subcategory} disabled={!stockForm.category}
                  onChange={e => setStockForm(p => ({ ...p, subcategory: e.target.value }))}>
                  <option value="">Alt Kategori</option>
                  {(partCatalog.subcategories[stockForm.category] || []).map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <input className={INP} placeholder="Parça Adı * (örn. Far Sol, Vites Kutusu)" value={stockForm.name} list="stock-part-names"
                onChange={e => setStockForm(p => ({ ...p, name: e.target.value }))} />
              <datalist id="stock-part-names">
                {(partCatalog.parts[`${stockForm.category}|${stockForm.subcategory}`] || []).map(n => <option key={n} value={n} />)}
              </datalist>
              <input className={INP} placeholder="OEM No" value={stockForm.oem_no}
                onChange={e => setStockForm(p => ({ ...p, oem_no: e.target.value }))} />
              <div className="grid grid-cols-2 gap-3">
                <select className={INP} value={stockForm.car_brand}
                  onChange={e => setStockForm(p => ({ ...p, car_brand: e.target.value, car_model: '' }))}>
                  <option value="">Araç Markası</option>
                  {VEHICLE_BRANDS.map(b => <option key={b}>{b}</option>)}
                </select>
                <select className={INP} value={stockForm.car_model} disabled={!stockForm.car_brand}
                  onChange={e => setStockForm(p => ({ ...p, car_model: e.target.value }))}>
                  <option value="">Araç Modeli</option>
                  {(VEHICLE_MODELS[stockForm.car_brand] || []).map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className={INP} inputMode="numeric" maxLength={4} placeholder="Yıl (başlangıç)" value={stockForm.year_from}
                  onChange={e => setStockForm(p => ({ ...p, year_from: e.target.value.replace(/\D/g, '').slice(0, 4) }))} />
                <input className={INP} inputMode="numeric" maxLength={4} placeholder="Yıl (bitiş)" value={stockForm.year_to}
                  onChange={e => setStockForm(p => ({ ...p, year_to: e.target.value.replace(/\D/g, '').slice(0, 4) }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className={INP} placeholder="Motor Kodu" value={stockForm.motor_code}
                  onChange={e => setStockForm(p => ({ ...p, motor_code: e.target.value.toUpperCase() }))} />
                <input className={INP} placeholder="Şanzıman Kodu" value={stockForm.gear_code}
                  onChange={e => setStockForm(p => ({ ...p, gear_code: e.target.value.toUpperCase() }))} />
              </div>
              <input className={INP} placeholder="Şasi No / VIN (çıkma parça için, isteğe bağlı)" maxLength={17}
                value={stockForm.chassis_no}
                onChange={e => setStockForm(p => ({ ...p, chassis_no: e.target.value.toUpperCase() }))} />
              <select className={INP} value={stockForm.condition}
                onChange={e => setStockForm(p => ({ ...p, condition: e.target.value }))}>
                {COND_OPTS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" className={INP} placeholder="Fiyat (₺) *" value={stockForm.price}
                  onChange={e => setStockForm(p => ({ ...p, price: e.target.value }))} />
                <input type="number" className={INP} placeholder="Stok Adedi" value={stockForm.stock_qty}
                  onChange={e => setStockForm(p => ({ ...p, stock_qty: parseInt(e.target.value) || 1 }))} />
              </div>
              <textarea className={`${INP} resize-y min-h-[60px]`} placeholder="Açıklama (isteğe bağlı)"
                value={stockForm.description} maxLength={500}
                onChange={e => setStockForm(p => ({ ...p, description: e.target.value }))} />
              <div>
                <label className="cursor-pointer flex items-center gap-2 border-2 border-dashed border-gray-200 hover:border-blue-400 rounded-lg px-3 py-2.5 text-sm text-gray-500">
                  📷 Fotoğraf ekle (en fazla 5)
                  <input type="file" accept="image/*" multiple className="hidden"
                    onChange={e => {
                      const picked = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
                      setStockPhotos(p => [...p, ...picked].slice(0, 5));
                      e.target.value = '';
                    }} />
                </label>
                {stockPhotos.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {stockPhotos.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="relative">
                        <img src={URL.createObjectURL(f)} alt="" className="w-14 h-14 rounded-lg object-cover border border-gray-200" />
                        <button type="button" onClick={() => setStockPhotos(p => p.filter((_, j) => j !== i))}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white text-[10px] flex items-center justify-center">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setStockModal(false); setStockErr(''); setStockPhotos([]); }}
                className="flex-1 border border-gray-200 text-gray-500 py-2.5 rounded-xl text-sm">İptal</button>
              <button onClick={saveStock} disabled={savingStock}
                className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-2.5 rounded-xl text-sm transition">
                {savingStock ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
